/** @jsx h */
import { h, Fragment } from "preact";
import { useContext, useEffect, useState } from "preact/hooks";
import { renderPreact } from "../lib/render-preact";
import PageContext from "./PageContext";
import { translate, t } from "../I18n";
import Alert from "./Alert";
import PreactTooltip from "./PreactTooltip";
import { formatRestaurantAddress } from "../utils/formatAddress";
import { formatOpeningHours } from "../utils/formatOpeningHours";
import { formatAddressAsSingleLine, formatFulfillmentMethodType, GeocodingService, LegacyStreetAddressStorageManager, LocalizationProvider, Money, RestaumaticRPCService, StreetAddress, StreetAddressFormController, StreetAddressStorageManager, WebGeolocationService, } from "restaumatic-client/src";
import { shouldEnableHrkDualPricesInCountry } from "../services/hrkDualPrices";
import { observer } from "mobx-react-lite";
import StreetAddressForm from "./StreetAddressForm";
import { LocateMeButton } from "./LocateMeButton";
const searchFormId = "section-restaurants";
const stickyHeaderSelector = ".js-sticky-header-element.is-affixed";
const getAddressSectionClass = (hasOverflowSection = false) => {
    return hasOverflowSection === true
        ? "m-restaurants__card--overflow-section"
        : "m-restaurants__card--regular-section";
};
// Scroll to the form if it's above the viewport.
const scrollToForm = (hasOverflowSection = false) => {
    const searchSection = document.getElementById(searchFormId);
    if (searchSection instanceof HTMLElement) {
        const stickyHeader = document.querySelector(stickyHeaderSelector);
        const overflowOffset = hasOverflowSection === true ? 90 : 0; // This value should match the CSS `margin-top' for `m-restaurants__card--overflow-section' element.
        const stickyHeaderOffset = stickyHeader instanceof HTMLElement ? stickyHeader.offsetHeight : 0;
        const scrollMarginTop = stickyHeaderOffset + overflowOffset + 15 + "px";
        const viewportOffset = searchSection.getBoundingClientRect().top;
        searchSection.style.scrollMarginTop = scrollMarginTop;
        if (viewportOffset < parseInt(scrollMarginTop)) {
            searchSection.scrollIntoView({
                behavior: "smooth",
            });
        }
    }
};
// Sort the list based on the 'isSelected' property (true value come first)
const sortRestaurants = (list) => {
    return list.sort((a, b) => {
        if (a.isSelected === b.isSelected) {
            return 0;
        }
        return a.isSelected ? -1 : 1;
    });
};
// Mark current restaurant as selected.
const updateRestaurantsWithLatest = (restaurants) => {
    if (typeof window === "undefined")
        return restaurants;
    const currentRestaurantSlug = window.localStorage.getItem("currentRestaurant");
    if (!currentRestaurantSlug)
        return restaurants;
    restaurants = restaurants.map((restaurant) => ({
        ...restaurant,
        isSelected: restaurant.slug === currentRestaurantSlug,
    }));
    return sortRestaurants(restaurants);
};
function toCardProps(localizationProvider, r) {
    var _a, _b, _c;
    return {
        id: r.id,
        name: r.name,
        slug: (_a = r.slug) !== null && _a !== void 0 ? _a : "",
        address: formatRestaurantAddress(r.address),
        phone: (_b = r.contactPhoneNumber) !== null && _b !== void 0 ? _b : "",
        email: (_c = r.contactEmail) !== null && _c !== void 0 ? _c : "",
        deliveryTypes: r.fulfillmentMethods
            .map(formatFulfillmentMethodType)
            .join(", "),
        menuLink: `/restauracja/${r.slug}`,
        isWithinArea: !!r.deliveryInfo,
        isSelected: false,
        openingHours: formatOpeningHours(r.restaurantHours),
        deliveryCost: r.deliveryCost !== null
            ? localizationProvider.formatMainPrice(Money.fromBackend(r.deliveryCost))
            : null,
        minOrderValue: r.minOrderValue !== null
            ? localizationProvider.formatMainPrice(Money.fromBackend(r.minOrderValue))
            : null,
        freeTreshold: r.freeDeliveryThreshold !== null
            ? localizationProvider.formatMainPrice(Money.fromBackend(r.freeDeliveryThreshold))
            : null,
    };
}
function RestaurantSelection(props) {
    const settings = useContext(PageContext);
    const localizationProvider = new LocalizationProvider({
        locale: settings.locale,
        fallbackLocale: settings.defaultLocale,
        hrkDualPricesEnabled: shouldEnableHrkDualPricesInCountry(settings.country),
        currency: settings.currency,
    });
    const [backendRestaurants, setBackendRestaurants] = useState(props.initialRestaurants);
    const restaurants = updateRestaurantsWithLatest(backendRestaurants.map((r) => toCardProps(localizationProvider, r)));
    const { hasOverflowSection, titleExtraClass, cardTitleExtraClass } = props;
    const [isFormVisible, setIsFormVisible] = useState(true);
    const [_loading, setLoading] = useState(false);
    const [geocodingError, setGeocodingError] = useState(false);
    const [streetAddress] = useState(() => StreetAddress.empty(settings.country));
    const [addressEntered, setAddressEntered] = useState(false);
    const currentAddress = addressEntered ? streetAddress : null;
    const deliveringRestaurants = restaurants.filter((item) => item.isWithinArea);
    const otherRestaurants = restaurants.filter((item) => !item.isWithinArea);
    const onEdit = () => {
        setIsFormVisible(true);
        scrollToForm(hasOverflowSection);
    };
    const rpcService = new RestaumaticRPCService({ url: "" });
    const geocodingService = new GeocodingService({
        rpcService: rpcService,
    });
    const onSubmit = () => {
        setLoading(true);
        setGeocodingError(false);
        setAddressEntered(true);
        new StreetAddressStorageManager(streetAddress.country).store(streetAddress);
        new LegacyStreetAddressStorageManager(streetAddress).store();
        geocodingService
            .geocode(settings.country, streetAddress)
            .then((coordinates) => {
            if (!coordinates) {
                setGeocodingError(true);
                return;
            }
            rpcService
                .rpc("GetRestaurants", {
                siteId: settings.siteId,
                location: coordinates,
            })
                .then((restaurants) => {
                setBackendRestaurants(restaurants);
                setIsFormVisible(false);
            })
                .finally(() => {
                setLoading(false);
            });
        });
    };
    // Restore from storage. Falls back to legacy storage for migration
    useEffect(() => {
        const restoredStreetAddress = new StreetAddressStorageManager(streetAddress.country).restore();
        if (restoredStreetAddress !== null) {
            streetAddress.city = restoredStreetAddress.city;
            streetAddress.street = restoredStreetAddress.street;
            streetAddress.streetNumber = restoredStreetAddress.streetNumber;
            streetAddress.city = restoredStreetAddress.city;
            streetAddress.postCode = restoredStreetAddress.postCode;
        }
        if (restoredStreetAddress !== null ||
            new LegacyStreetAddressStorageManager(streetAddress).restore()) {
            onSubmit();
        }
    }, []);
    return (h("div", { className: "m-restaurants", "aria-live": "polite" },
        h("a", { name: "RestaurantSelection" }),
        h("article", { className: "u-base-spacing-b" },
            isFormVisible && (h(AddressForm, { titleExtraClass: titleExtraClass, hasOverflowSection: hasOverflowSection, geocodingError: geocodingError, streetAddress: streetAddress, onSubmit: onSubmit })),
            currentAddress && !isFormVisible && (h(FilledAddressBox, { titleExtraClass: titleExtraClass, hasOverflowSection: hasOverflowSection, selectedDeliveryType: "Dostawa", onEdit: onEdit, address: currentAddress }))),
        currentAddress === null && (h(RestaurantList, { title: translate(t.restaurants.select.header_all), list: restaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, address: currentAddress })),
        currentAddress !== null && (h(Fragment, null,
            h(RestaurantList, { title: translate(t.restaurants.select.header_deliver), list: deliveringRestaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, doesRestaurantDeliver: true, onEdit: !isFormVisible ? onEdit : undefined, address: currentAddress }),
            h(RestaurantList, { title: translate(t.restaurants.select.header_others), list: otherRestaurants, titleExtraClass: titleExtraClass, cardTitleExtraClass: cardTitleExtraClass, doesRestaurantDeliver: false, onEdit: onEdit, address: currentAddress })))));
}
const AddressForm = observer((props) => {
    const { titleExtraClass, hasOverflowSection, onSubmit, geocodingError, streetAddress, } = props;
    const formExtraClass = getAddressSectionClass(hasOverflowSection);
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit();
    };
    const [controller] = useState(() => {
        const geocodingService = new GeocodingService({
            rpcService: new RestaumaticRPCService({ url: "" }),
        });
        const geolocationService = new WebGeolocationService();
        return new StreetAddressFormController({
            streetAddress,
            geocodingService,
            geolocationService,
        });
    });
    const [geolocationError, setGeolocationError] = useState(null);
    return (h("div", { className: `m-restaurants__card ${formExtraClass}` },
        h("h2", { className: `m-restaurants__heading ${titleExtraClass}`, id: "restaurant-selection-form-heading" }, translate(t.restaurants.select.title_find)),
        h("form", { "aria-controls": "restaurant-selection-list", onSubmit: handleSubmit },
            h(LocateMeButton, { onLocateMe: () => {
                    setGeolocationError(null);
                    controller
                        .locateMe()
                        .then((success) => {
                        if (success) {
                            onSubmit();
                        }
                        else {
                            setGeolocationError(translate(t.geolocator.address_missing));
                        }
                    })
                        .catch((e) => {
                        if (e instanceof GeolocationPositionError) {
                            setGeolocationError(e.code === GeolocationPositionError.PERMISSION_DENIED
                                ? translate(t.geolocator.no_permission)
                                : translate(t.geolocator.failed_to_locate));
                        }
                    });
                }, geolocationError: geolocationError !== null && geolocationError !== void 0 ? geolocationError : undefined }),
            h("div", { className: "m-delivery-address__container" },
                h(StreetAddressForm, { controller: controller })),
            h("button", { type: "submit", className: "btn btn-primary" }, translate(t.restaurants.select.find_restaurant))),
        geocodingError && (h(Alert, { type: "danger", message: translate(t.delivery.geocoding_error) }))));
});
// TODO: To add in MVP (current version in based on existing layout from `selectRestaurant.purs`)
function FilledAddressBox(props) {
    const { hasOverflowSection, selectedDeliveryType, titleExtraClass, onEdit, address, } = props;
    const formExtraClass = getAddressSectionClass(hasOverflowSection);
    const editBtnClass = hasOverflowSection === true ? "u-cap-first-letter pull-right" : "";
    const editBtnText = hasOverflowSection === true
        ? translate(t.restaurants.select.button_change)
        : "(" + translate(t.restaurants.select.button_change) + ")";
    return (h("div", { className: `m-restaurants__card ${formExtraClass}` },
        h("h2", { className: `m-restaurants__heading ${titleExtraClass}` }, translate(t.restaurants.select.your_address)),
        h("div", { className: "d-flex" },
            h("span", { className: "m-restaurants__card-icon m-restaurants__card-icon--primary icon-location icon-base", "aria-hidden": "true" }),
            h("div", { className: "u-flex-grow" },
                h("b", null, selectedDeliveryType),
                h("br", null),
                h("span", null,
                    formatAddressAsSingleLine(address, address.isPostCodeRequired),
                    "\u00A0"),
                h("button", { type: "button", className: `btn btn-link u-p0 ${editBtnClass}`, onClick: onEdit }, editBtnText)))));
}
function RestaurantList(props) {
    const { title, list, titleExtraClass, cardTitleExtraClass, doesRestaurantDeliver, onEdit, address, } = props;
    if (list.length === 0) {
        return h(Fragment, null);
    }
    return (h("article", { className: "u-base-spacing-b" },
        h("h2", { className: `m-restaurants__heading ${titleExtraClass}`, id: "restaurant-selection-list-heading" },
            h("span", null,
                title,
                " "),
            address && doesRestaurantDeliver !== false && (h("span", null, formatAddressAsSingleLine(address, address.isPostCodeRequired)))),
        h("ul", { className: "list-unstyled row row--flex row--vertical-gutter", id: "restaurant-selection-list", "aria-labelledby": "restaurant-selection-list-heading" }, list.map((item) => (h(RestaurantCard, { restaurant: item, titleExtraClass: cardTitleExtraClass, doesRestaurantDeliver: doesRestaurantDeliver, onEdit: onEdit, address: address }))))));
}
function RestaurantCard(props) {
    const { restaurant, doesRestaurantDeliver, titleExtraClass, onEdit, address, } = props;
    const openingHours = restaurant.openingHours.split("\n");
    const freeTresholdString = restaurant.freeTreshold
        ? ", " +
            translate(t.theme_defaults.delivery.info.free_treshold) +
            restaurant.freeTreshold
        : "";
    return (h("li", { className: "col-sm-6 col-md-4" },
        h("div", { class: `m-restaurants__card ${restaurant.isSelected ? "m-restaurants__card--selected" : ""}` },
            h("h3", { className: `m-restaurants__card-title ${titleExtraClass}` }, restaurant.name),
            h("ul", { className: "u-list-unstyled u-flex-grow" },
                h("li", { className: "d-flex u-my3" }, restaurant.address),
                h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-time icon-base", "aria-hidden": "true" }),
                    h("div", null, openingHours.map((line, index) => (h(Fragment, { key: index },
                        line,
                        h("br", null)))))),
                restaurant.phone && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-phone icon-base", "aria-hidden": "true" }),
                    h("a", { href: `tel:${restaurant.phone}`, className: "u-link-unstyled" }, restaurant.phone))),
                restaurant.email && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-mail icon-base", "aria-hidden": "true" }),
                    h("a", { href: `mailto:${restaurant.email}`, className: "u-link-unstyled u-text-ellipsis" }, restaurant.email))),
                h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-shopping-cart icon-base", "aria-hidden": "true" }),
                    h("span", null, restaurant.deliveryTypes)),
                doesRestaurantDeliver === true && (h("li", { className: "d-flex u-my3" },
                    h("span", { className: "m-restaurants__card-icon icon-truck icon-base", "aria-hidden": "true" }),
                    h("span", null,
                        restaurant.deliveryCost,
                        " (min. ",
                        restaurant.minOrderValue,
                        ")",
                        h(PreactTooltip, { class: "btn btn-link u-p0 u-text-decoration-none", title: `${translate(t.theme_defaults.delivery.info.cost)}${restaurant.deliveryCost}, ${translate(t.theme_defaults.delivery.info.min_order_value)}${restaurant.minOrderValue}${freeTresholdString}` },
                            h("span", { className: "icon-info-sign icon-flex-center", "aria-label": translate(t.theme_defaults.theme.show_more) })))))),
            address && doesRestaurantDeliver === false && (h(NoWithinAreaAlert, { onEdit: onEdit, address: address })),
            h("div", null,
                h("a", { href: restaurant.menuLink, className: "m-restaurants__card-action btn btn-primary" }, translate(t.restaurants.select.button_select))))));
}
function NoWithinAreaAlert(props) {
    const { onEdit, address } = props;
    return (h(Fragment, null,
        h(Alert, { type: "warning", message: translate(t.delivery.not_possible, {
                address: formatAddressAsSingleLine(address, address.isPostCodeRequired),
            }), link: {
                label: `(${translate(t.restaurants.select.button_change)})`,
                path: "#" + searchFormId,
                onClick: onEdit
                    ? (e) => {
                        e.preventDefault();
                        onEdit();
                    }
                    : undefined,
            } })));
}
export default renderPreact("RestaurantSelection", observer(RestaurantSelection));
